import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { CaretLeftFill, CaretRightFill, SkipStartFill, SkipEndFill, CircleHalf } from "react-bootstrap-icons";
import axios from "axios";

import ConfirmationDialog from "../dialog/ConfirmationDialog";
import Notification from "../dialog/Notification";
import PhotoThumbnail from "./PhotoThumbnail";

// IMPORT setGalleryPage to save state
import { loadPhotosGallery, setGalleryPage } from "../../../features/gallery/gallerySlice";
import { getUserState, getGalleryState } from "../../../app/selector";
import { ReactComponent as Do } from "../../../resources/do.svg";
import { ReactComponent as DoNot } from "../../../resources/do-not.svg";
import { BACKEND_URL } from "constants";

import styles from "./PhotoViewer.module.css";

const profilePicturePlaceholder = require("../../../resources/ProfilePicturePlaceholder.jpg");

const calculatePhotosPerPage = () => {
  const screenWidth = Math.min(window.screen.width, window.screen.height);
  const screenHeight = Math.max(window.screen.width, window.screen.height);
  const isMobileWidth = screenWidth <= 768;
  const isTallScreen = screenHeight >= 840;

  if (isMobileWidth) {
    return isTallScreen ? 12 : 10;
  }
  return 12;
};

const PhotoViewer = ({ 
  photosToShow = null, 
  title = null, 
  allowOperations = true, 
  showGenderFilter = false 
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useSelector(getUserState);
  
  // EXTRACT currentPage FROM REDUX (rename it to reduxPage locally to avoid conflicts)
  const { photosGalleryLoaded, photoGalleryById, currentPage: reduxPage } = useSelector(getGalleryState);

  const [showDialog, setShowDialog] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [success, setSuccess] = useState(true);
  const [photoToHide, setPhotoToHide] = useState(null);
  const [photosPerPage, setPhotosPerPage] = useState(calculatePhotosPerPage());

  // Filter State
  const [genderFilter, setGenderFilter] = useState("ALL");

  // Initialize currentPage from location state OR Redux OR default to 1
  const [currentPage, setCurrentPage] = useState(() => {
    return location.state?.fromPage || location.state?.targetPage || reduxPage || 1;
  });

  // SAVE CURRENT PAGE TO REDUX WHENEVER IT CHANGES
  useEffect(() => {
    dispatch(setGalleryPage(currentPage));
  }, [currentPage, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setPhotosPerPage(calculatePhotosPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const galleryPhotosArray = useMemo(() => {
    return Object.values(photoGalleryById);
  }, [photoGalleryById]);

  const photos = useMemo(() => {
    return photosToShow !== null ? photosToShow : galleryPhotosArray;
  }, [photosToShow, galleryPhotosArray]);

  const filteredPhotos = useMemo(() => {
    if (!showGenderFilter || genderFilter === "ALL") return photos;
    return photos.filter(
      (photo) => photo.gender?.toLowerCase() === genderFilter.toLowerCase()
    );
  }, [photos, genderFilter, showGenderFilter]);

  const totalPages = Math.ceil(filteredPhotos.length / photosPerPage);

  // BUGGY useEffect REMOVED HERE: We no longer reset the page automatically just because genderFilter exists.

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const currentPhotos = useMemo(() => {
    const start = (currentPage - 1) * photosPerPage;
    return filteredPhotos.slice(start, start + photosPerPage);
  }, [filteredPhotos, currentPage, photosPerPage]);

  const reloadPhotosGallery = useCallback((force = false) => {
    if (user && (force || !photosGalleryLoaded)) {
      axios
        .post(`${BACKEND_URL}/photos/gallery`, { user_id: user.user_id })
        .then((response) => {
          dispatch(loadPhotosGallery({ photos: response.data }));
        })
        .catch(console.error);
    }
  }, [dispatch, photosGalleryLoaded, user]);

  useEffect(() => {
    if (photosToShow === null) {
      reloadPhotosGallery();
    }
  }, [photosToShow, reloadPhotosGallery]);

  const handleHidePhotoTrigger = (photo) => {
    setPhotoToHide(photo);
    setShowDialog(true);
  };

  const handleConfirmHide = () => {
    if (!photoToHide) return;

    const payload = {
      sender_user_id: user.user_id,
      sender_photo_id: null,
      receiver_user_id: photoToHide.user_id,
      receiver_photo_id: photoToHide.photo_id,
      accepted: null,
      origin: "gallery",
      type: "not",
    };

    axios
      .post(`${BACKEND_URL}/request/create`, payload)
      .then(() => {
        setShowDialog(false);
        setSuccess(true);
        setShowNotification(true);
      })
      .catch((error) => {
        setShowDialog(false);
        setSuccess(false);
        setShowNotification(true);
        console.error(error);
      });
  };

  const handleNotificationClose = () => {
    setShowNotification(false);
    if (photosToShow !== null) {
      navigate(0);
    } else {
      reloadPhotosGallery(true);
    }
  };

  // Pagination Functions
  const handleBegin = () => setCurrentPage(1);
  const handleHalf = () => setCurrentPage(Math.max(1, Math.ceil(totalPages / 2)));
  const handleEnd = () => setCurrentPage(totalPages);
  const handlePrev = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  return (
    <div className={styles["photo-viewer"]}>
      <ConfirmationDialog
        text="Don't you do the same?"
        show={showDialog}
        setShow={setShowDialog}
        operation="I don't do the same!"
        variant="danger"
        onConfirm={handleConfirmHide}
      />

      <Notification
        text={success ? "You don't do the same!" : "Something went wrong!"}
        variant={success ? "secondary" : "danger"}
        show={showNotification}
        setShow={setShowNotification}
        onClose={handleNotificationClose}
      />

      {(title || showGenderFilter) && (
        <div className={styles["viewer-header"]}>
          <div className={styles["header-spacer"]}></div>
          {title ? <h1 className="menu-title mb-0">{title}</h1> : <div></div>}
          
          <div className={styles["dropdown-wrapper"]}>
            {showGenderFilter && (
              <select
                className={styles["gender-select"]}
                value={genderFilter}
                onChange={(e) => {
                  setGenderFilter(e.target.value);
                  setCurrentPage(1); // CORRECT RESET: Only resets when the user interacts with the dropdown
                }}
                aria-label="Filter by gender"
              >
                <option value="ALL">All</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            )}
          </div>
        </div>
      )}

      {currentPhotos.length > 0 ? (
        <div className={styles["grid-container"]}>
          {currentPhotos.map((photo, index) => {
            const src = photo.photo ? `data:image/jpeg;base64,${photo.photo}` : profilePicturePlaceholder;
            const itemKey = photo.photo_id || `${photo.user_id}-${index}`;

            const isDoButton = photo.sender_user_id === user.user_id && photo.type === "do" && photo.gallery_enabled === "1";
            const isDoNotButton = photo.sender_user_id === user.user_id && photo.type === "not" && photo.gallery_enabled === "1";

            return (
              <div className={styles["grid-item"]} key={itemKey}>
                <PhotoThumbnail src={src} photoId={photo.photo_id} />

                {allowOperations && (
                  <div className={styles["button-group"]}>
                    <Button
                      variant={isDoButton ? "success" : "outline-success"}
                      disabled={isDoButton}
                      className="round-button"
                      onClick={() => navigate(`/send-request/${photo.photo_id}`, { state: { fromPage: currentPage } })}
                    >
                      <Do />
                    </Button>

                    <Button
                      variant={isDoNotButton ? "danger" : "outline-danger"}
                      disabled={isDoNotButton}
                      className="round-button"
                      onClick={() => handleHidePhotoTrigger(photo)}
                    >
                      <DoNot />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <Form.Label className="d-block text-center mt-4">
          {showGenderFilter && genderFilter !== "ALL" 
            ? "No photos match your filter" 
            : "No photos found"}
        </Form.Label>
      )}

      {totalPages > 1 && (
        <div className={styles["pagination-container"]}>
          <div className={styles["pagination-controls"]}>
            <Button
              variant="light"
              className={styles["pagination-btn"]}
              onClick={handleBegin}
              disabled={currentPage === 1}
              aria-label="First page"
            >
              <SkipStartFill size={16} /> <span className={styles["btn-text"]}>Begin</span>
            </Button>

            <Button
              variant="light"
              className={styles["pagination-btn"]}
              onClick={handlePrev}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <CaretLeftFill size={16} /> <span className={styles["btn-text"]}>Prev</span>
            </Button>

            <Button
              variant="light"
              className={`${styles["pagination-btn"]} ${styles["half-combined-btn"]}`}
              onClick={handleHalf}
              disabled={totalPages < 2}
              title="Jump to middle page"
            >
              <CircleHalf size={14} className={styles["mid-icon"]} />
              <span className={styles["status-divider"]}></span>
              <span className={styles["combined-status-text"]}>
                <span className={styles["status-highlight"]}>{currentPage}</span> / {totalPages}
              </span>
            </Button>

            <Button
              variant="light"
              className={styles["pagination-btn"]}
              onClick={handleNext}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <span className={styles["btn-text"]}>Next</span> <CaretRightFill size={16} />
            </Button>

            <Button
              variant="light"
              className={styles["pagination-btn"]}
              onClick={handleEnd}
              disabled={currentPage === totalPages}
              aria-label="Last page"
            >
              <span className={styles["btn-text"]}>End</span> <SkipEndFill size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoViewer;